import { useCallback, useEffect } from "react";

/**
 * "Have the dashboard updates been seen?" — backs the unread dot on the
 * dashboard's notification bell.
 *
 * Tracked as a local timestamp rather than the notification's own read status
 * on purpose. `GET /notifications/info` only returns notifications with
 * status PENDING, so marking them read would remove them from the Updates
 * screen entirely; their lifecycle belongs to the backend scheduler, not to
 * whether the user glanced at the bell. Seen-ness is a per-device viewing
 * concern, so it lives on the device.
 *
 * Keyed per member so switching accounts on a shared device doesn't inherit
 * the other account's seen state.
 *
 * The `persist_` prefix is required, not cosmetic: `clearStoragePreservingTours`
 * in `@/utils/storage-utils` wipes every localStorage key on logout except a
 * short allow-list that `persist_*` is part of. Without it the stamp resets to
 * "never seen" on every logout, so the dot reappeared on each login for
 * notifications the user had already read.
 */
const STORAGE_PREFIX = "persist_dashboardUpdatesSeenAt";

/**
 * Pre-`persist_` key name. Read-only fallback so users already logged in when
 * this shipped keep their existing seen state instead of getting one spurious
 * dot. Reads are pure on purpose (no rewrite here — this runs during render);
 * the new key gets written the next time Updates is opened, and logout clears
 * the stale legacy key on its own.
 */
const LEGACY_STORAGE_PREFIX = "dashboardUpdatesSeenAt";

function storageKey(memberId?: string): string | null {
  return memberId ? `${STORAGE_PREFIX}:${memberId}` : null;
}

function parseTimestamp(raw: string | null): number {
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function readSeenAt(memberId?: string): number {
  if (!memberId) return 0;
  try {
    const seenAt = parseTimestamp(
      localStorage.getItem(`${STORAGE_PREFIX}:${memberId}`)
    );
    if (seenAt) return seenAt;
    return parseTimestamp(
      localStorage.getItem(`${LEGACY_STORAGE_PREFIX}:${memberId}`)
    );
  } catch {
    // Private mode / storage disabled — fall back to "never seen" so the dot
    // still shows. Better a persistent dot than a silently missed update.
    return 0;
  }
}

/**
 * True when any notification arrived after the last time the Updates screen
 * was opened. With no stored timestamp, any notification counts as unseen.
 */
export function useHasUnseenUpdates(
  memberId: string | undefined,
  notifications: Array<{ createdAt?: string }>
): boolean {
  const seenAt = readSeenAt(memberId);
  return notifications.some((notification) => {
    if (!notification?.createdAt) return true;
    const createdAt = Date.parse(notification.createdAt);
    return Number.isNaN(createdAt) ? true : createdAt > seenAt;
  });
}

/**
 * Stamps "seen" once the Updates screen has actually rendered its list.
 * Waits for loading to finish so a screen the user never saw content on
 * doesn't suppress the dot.
 */
export function useMarkUpdatesSeen(
  memberId: string | undefined,
  notifications: Array<{ createdAt?: string }>,
  loading: boolean
): void {
  const mark = useCallback(() => {
    const key = storageKey(memberId);
    if (!key) return;
    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // Non-fatal: the dot simply stays until storage works again.
    }
  }, [memberId]);

  useEffect(() => {
    if (loading || !memberId) return;
    mark();
  }, [loading, memberId, notifications.length, mark]);
}
